import type { Logger } from "@expert/logging";
import type { OpenGuideSource } from "@expert/solve-tools";

import { addGuideRecommendationMessage } from "@asurion-core/expert-workspace-timeline";
import { useFeatures, type WorkspaceFeatures } from "@expert/features";
import { trackFullStoryEvent } from "@expert/monitoring";
import {
    guidedFlowRecommendationMap,
    isGuideRecommendationDisabled,
    isGuideSupported,
    setRecommendation,
    useGuideStore,
} from "@expert/solve-tools";
import { useEffect, useMemo } from "react";

import { getRootDispatcher } from "@/analytics";

export const useGuideRecommendation = ({
    callSid,
    isGuideOpen,
    logger,
    openGuide,
    sessionId,
}: {
    callSid?: string;
    isGuideOpen: boolean;
    logger: Logger;
    openGuide: (source: OpenGuideSource, guide?: string) => void;
    sessionId: string;
}) => {
    const rootDispatcher = getRootDispatcher();

    const enhancedDispatcher = useMemo(
        () => (callSid ? rootDispatcher.withIdentities({ CallSid: callSid }) : rootDispatcher),
        [callSid, rootDispatcher],
    );

    const {
        features: { isGuidedFlowRecommendationEnabled },
    } = useFeatures<WorkspaceFeatures>();

    const { analyticsInfo, disableAutomaticOpen, recommendation } = useGuideStore();

    useEffect(() => {
        if (!isGuidedFlowRecommendationEnabled) return;
        if (!recommendation) return;

        const guideToAutoOpen = guidedFlowRecommendationMap[recommendation];
        if (!guideToAutoOpen) {
            logger.info({ recommendation }, "Solve Guide | unknown recommendation, clearing");
            setRecommendation(logger, undefined);
            return;
        }

        if (isGuideOpen || disableAutomaticOpen) {
            logger.info(
                { disableAutomaticOpen, isGuideOpen, recommendation },
                "Solve Guide | recommendation received, but not opening guide",
            );
            setRecommendation(logger, undefined);
            return;
        }

        // Defer until the Simplr session has loaded the GuideSelection step. Opening sooner produces
        // an empty panel because availableGuides may be stale-from-persist while the new session's
        // messages haven't loaded yet, and automaticallySelectGuideIfNeeded silently no-ops unless
        // currentStepId === "GuideSelection".
        if (analyticsInfo.currentStepId !== "GuideSelection") {
            logger.info(
                { currentStepId: analyticsInfo.currentStepId, recommendation },
                "Solve Guide | recommendation pending Simplr session readiness",
            );
            return;
        }

        const guideSupported = isGuideSupported(guideToAutoOpen);
        const guideDisabled = isGuideRecommendationDisabled(guideToAutoOpen);
        if (!guideSupported || guideDisabled) {
            logger.info(
                { isGuideRecommendationDisabled: guideDisabled, isGuideSupported: guideSupported, recommendation },
                "Solve Guide | not opening guide - either not supported or disabled",
            );
            setRecommendation(logger, undefined);
            return;
        }

        logger.info({ recommendation }, "Solve Guide | auto opening guide for recommendation");
        void enhancedDispatcher.dispatchBusinessEvent("Guide_RecommendationMessageShown", {
            guide: guideToAutoOpen,
        });
        trackFullStoryEvent("Guide_RecommendationMessageShown", { guide: guideToAutoOpen });
        addGuideRecommendationMessage(guideToAutoOpen, sessionId);
        openGuide({ source: "auto", sourceDetails: "recommendation" }, guideToAutoOpen);
        setRecommendation(logger, undefined);
    }, [
        analyticsInfo.currentStepId,
        disableAutomaticOpen,
        enhancedDispatcher,
        isGuideOpen,
        isGuidedFlowRecommendationEnabled,
        logger,
        openGuide,
        recommendation,
        sessionId,
    ]);
};
